import { IconButton, Skeleton } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import { useAtom } from 'jotai';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { rewriteEditModalAtom, selectedRewriteAtom } from '../../atoms/rewrite';
import RewriteClient from '../../clientAPI/rewrite';
import { QUERY_KEYS, REWRITE_TYPE } from '../../constants';
import { isExistScope } from '../../utils/authHelpers';
import propagate403Error from '../../utils/propagate403Error';

const ItemTargetComponent = ({ data }: { data: RewriteItem }) => {
  if (data.type === 'T2C') {
    return (
      <>
        {(data.target as RewriteTarget[]).map((t, idx) => (
          <span
            key={`${t.id}_${idx}`}
            className={`tw-bg-blue-100 tw-text-blue-800 tw-ml-2 tw-px-2.5 tw-py-0.5 tw-rounded tw-border tw-border-blue-400 tw-text-xs`}
          >
            {t.name}
          </span>
        ))}
      </>
    );
  }
  return (
    <span className='tw-bg-green-100 tw-text-green-800 tw-ml-2 tw-px-2.5 tw-py-0.5 tw-rounded tw-border tw-border-green-400 tw-text-xs'>
      {data.target as string}
    </span>
  );
};

function ItemCtr({ data }: { data: RewriteItem }) {
  const [dataItem, setDataItem] = useAtom(selectedRewriteAtom);
  const [visible, setVisible] = useAtom(rewriteEditModalAtom);
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:rewrite:write', jwt);

  const termCls = classnames(
    'tw-leading-7 group-hover:tw-text-xl , group-hover:tw-font-bold tw-pl-1 tw-pr-1',
    {
      'tw-line-through tw-decoration-slate-400 tw-text-slate-400':
        !data.enabled,
      'tw-text-indigo-700': data.type === REWRITE_TYPE.T2C,
      'tw-text-green-700': data.type === REWRITE_TYPE.T2T,
      'tw-text-pink-700': data.rewriteTags.some((rt) => rt.tagName === 'typo'),
    },
  );

  return (
    <div
      onClick={() => {
        if (canWrite) {
          setDataItem(data);
          setVisible(true);
        }
        return;
      }}
      className='eds-type--body-2 hover:tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-group tw-flex-wrap tw-w-96 hover:tw-shadow'
    >
      <div className={termCls}>{data.term}</div>
      <div className='tw-flex tw-items-center tw-justify-end tw-flex-wrap group-hover:tw-font-bold'>
        <ItemTargetComponent data={data} />
      </div>
    </div>
  );
}

export default function SharedAlphabetCard({
  type,
  label,
  paginationSize = 80,
  indexKey,
}: {
  type: 'rewrite';
  label: string;
  paginationSize: number;
  indexKey: string;
}) {
  const [currentPage, setCurrentPage] = useState('');
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();
  const apiClient = RewriteClient;
  const queryKey = QUERY_KEYS.REWRITE_LIST;
  const { status, data, error } = useQuery({
    queryKey: [queryKey, indexKey, paginationSize, currentPage],
    queryFn: async () => {
      if (currentPage) {
        return apiClient.next(currentPage);
      } else {
        return apiClient.listByParams({ count: paginationSize, indexKey });
      }
    },
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });
  propagate403Error(error as keyable);
  if (status === 'loading' && !currentPage) {
    return <Skeleton height={700} width={350} className='eds-spacing--mr-16' />;
  }
  if (status === 'success') {
    const shouldPaginate = !!data.pages.next || !!data.pages.prev;
    return (
      <div className='tw-px-4 tw-py-3 tw-rounded tw-border-2 tw-border-slate-500 tw-bg-white'>
        <div className='tw-uppercase eds-type--subtitle-1 tw-text-left'>
          {label}
        </div>
        <div className='tw-text-left'>
          {data.objects.map((rw, idx) => {
            return <ItemCtr key={rw._id} data={rw} />;
          })}
        </div>
        {shouldPaginate && (
          <div className='tw-flex tw-items-center tw-justify-between tw-mt-6'>
            <IconButton
              icon='ArrowLeft'
              size='small'
              disabled={!data.pages.prev}
              onClick={() => {
                setCurrentPage(data.pages.prev);
              }}
              label='previous'
              variant='ghost'
            />
            <IconButton
              icon='ArrowRight'
              size='small'
              disabled={!data.pages.next}
              onClick={() => {
                setCurrentPage(data.pages.next);
              }}
              label='next'
              variant='ghost'
            />
          </div>
        )}
      </div>
    );
  }
  return <></>;
}

SharedAlphabetCard.propTypes = {
  label: PropTypes.string,
  indexKey: PropTypes.string,
};
